import { Component, Input, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs/operators';
import { BxDocumentSourceType } from '../../../core/components/bx-documents/documents.model';
import { CataloguesService } from '../../../core/services/catalogues.service';
import { EditTrackerService } from '../../../core/services/edit-tracker.service';
import { LaunchDarklyService } from '../../../core/services/launchdarkly.service';
import { FeatureService, PermissionService } from '../../../core/services/permission';
import { Integration } from '../../../data/models/integrations.model';
import { RepoAccountingSyncService } from '../../../data/repositories/repo-accountingSync.service';
import { RepoClientsService } from '../../../data/repositories/repo-clients.service';
import { RepoCommonService } from '../../../data/repositories/repo-common.service';
import { RepoIntegrationsService } from '../../../data/repositories/repo-integrations.service';
import { SharedDataService } from '../../../data/services/shared-data.service';
import { getAddressComponents } from '../../../utils/helper-functions/address.functions';
import { LayoutService } from '../../layout/layout.service';
import { ClientsService } from '../clients.service';
import { appConfig } from './../../../app.config';
import { IntegrationGroup } from './../../integrations-v2/integrations.enum';

interface AccountingContact {
    description: string;
    firstName: string;
    lastName: string;
    id: string;
}

@UntilDestroy()
@Component({
    selector: 'app-client-details',
    templateUrl: './client-details.component.html',
    standalone: false,
})
export class ClientDetailsComponent implements OnInit {
    // @ts-ignore TS7008
    @Input() customer;

    // @ts-ignore TS7008
    config;
    // @ts-ignore TS7008
    dateOptions;
    // @ts-ignore TS7008
    isSupplier;
    // @ts-ignore TS7008
    userType;
    title = 'Customer Details Controller';
    notes = [];
    // @ts-ignore TS2564
    exists: boolean;

    // @ts-ignore TS7008
    documentsParams;
    initialExternalRef = null;
    initialExternalRef2 = null;
    SupplierExternalRefFieldName = '';
    SupplierExternalRefFieldName2 = '';
    SupplierExternalRefRequired2 = false;
    // @ts-ignore TS7008
    accountingProvider;
    accountingContacts: AccountingContact[] = [];
    // @ts-ignore TS2564
    locale: string;
    // @ts-ignore TS7008
    estimateSources;
    // @ts-ignore TS7008
    items;
    clientSource = BxDocumentSourceType.Clients;

    editModes = {
        customer: false,
        contacts: false,
    };

    autocompleteOptions = {
        componentRestrictions: {
            country: 'au',
        },
        types: ['geocode'],
    };

    permissions = {
        editCustomerDetails: false,
        viewContacts: false,
        addContacts: false,
        viewDocuments: false,
        addDocuments: false,
        deleteDocuments: false,
        viewNotes: false,
        addNotes: false,
        deleteNotes: false,
    };

    constructor(
        private cataloguesService: CataloguesService,
        private translateService: TranslateService,
        private clientsService: ClientsService,
        private layoutService: LayoutService,
        public sharedData: SharedDataService,
        private customersRepo: RepoClientsService,
        private editTracker: EditTrackerService,
        private permissionService: PermissionService,
        private featureService: FeatureService,
        private accountingSync: RepoAccountingSyncService,
        private repoCommon: RepoCommonService,
        private integrationsService: RepoIntegrationsService,
        private launchDarkly: LaunchDarklyService
    ) {}

    ngOnInit() {
        this.config = appConfig().config;
        this.dateOptions = this.config.DATEPICKER;
        this.translateService.get('general.dateFormat').subscribe((res) => {
            this.locale = res;
        });

        this.isSupplier = this.featureService.isAvailable('merchant');
        this.featureService.userType$.pipe(untilDestroyed(this)).subscribe((userType) => {
            this.userType = userType;

            this.initialExternalRef = this.customer.ExternalRef;
            this.initialExternalRef2 = this.customer.ExternalRef2;

            this.documentsParams = {
                reference: this.customer.ID_Customer,
                sourceType: 'Clients',
            };

            if (this.isSupplier) this.getSupplierExternalRefFieldName();

            this.repoCommon.GetEstimateSources().then((results: any) => {
                this.estimateSources = results
                    // @ts-ignore TS7006
                    .map((group) => {
                        return group.Description;
                    })
                    .sort();
            });

            this.loadContacts();

            this.items = [
                {
                    description: '10March2020',
                    firstName: null,
                    lastName: null,
                    id: '019893f0-5be3-4237-99e0-ddcb89eae455',
                },
                {
                    description: '10March20201',
                    firstName: null,
                    lastName: null,
                    id: '5d6cb39c-a1b2-443d-8cbc-7fc5501ca224',
                },
            ];
        });

        this.setPermissions();
    }

    // @ts-ignore TS7006
    triggerEditMode(editModes, type, callback) {
        editModes[type] = !editModes[type];
        if (typeof callback === 'function') {
            callback();
        }
    }

    saveDetails() {
        this.saveChanges();
    }

    isSecondaryNav() {
        return this.layoutService.showSecondaryNav();
    }

    // @ts-ignore TS7006
    getEncodedAddress(address) {
        return encodeURIComponent(address);
    }

    checkCustomerNameUnique(f: NgForm) {
        this.customersRepo.Exists(this.customer.Name, this.customer.ID_Customer).then((exists) => {
            this.exists = exists;
            const ctrl = f.controls['Name'];
            let errors;
            errors = this.exists ? { existingName: 'Name already exists' } : null;

            // check for blanks
            // @ts-ignore TS2532
            errors = ctrl.value.trim() === '' ? { blankValue: 'Enter valid name' } : null;

            // @ts-ignore TS2532
            ctrl.setErrors(errors);
        });
    }

    saveChanges() {
        this.customersRepo.Save(null, 'Saved').then(() => {
            this.customersRepo.Update(this.customer.ID_Customer);
            if (this.customer.ExternalRef !== this.initialExternalRef || this.customer.ExternalRef2 !== this.initialExternalRef2) {
                this.cataloguesService
                    .updateIntegrationConnectionExternalRefV2(
                        this.customer.ID_Customer,
                        this.customer.ExternalRef,
                        this.customer.ExternalRef2
                    )
                    .subscribe(() => {
                        this.initialExternalRef = this.customer.ExternalRef;
                        this.initialExternalRef2 = this.customer.ExternalRef2;
                    });
            }
            this.editModes.customer = false;
            this.editTracker.stop();
        });
    }

    // @ts-ignore TS7006
    showClientsForm(d) {
        this.editModes.customer = true;
        this.editTracker.start(d, false);
    }

    cancelClientsForm() {
        this.exists = false;
        this.customer.entityAspect.rejectChanges();
        this.editModes.customer = false;
        this.editTracker.stop();
    }

    addClient() {
        const newClient = this.customersRepo.CreateContact(this.customer);
        this.clientsService
            .viewClient(newClient)
            .then(() => ({}))
            .catch(() => ({}));
    }

    getSupplierExternalRefFieldName() {
        this.cataloguesService
            .getIntegrationProviders(false, false, this.userType === 'Supplier' ? IntegrationGroup.Supplier : IntegrationGroup.Manufacturer)
            .pipe(first())
            .subscribe((res) => {
                const providers = res.data.integrationProviders['nodes'];
                const provider = providers.find((x) => x.iD_Tenant === this.sharedData.UserData.tenant.ID_Tenant);
                if (provider) {
                    this.SupplierExternalRefFieldName = provider.refFieldName;
                    this.SupplierExternalRefFieldName2 = provider.refFieldName2;
                    this.SupplierExternalRefRequired2 = provider.requireRef2;
                }
            });
    }

    loadContacts() {
        this.integrationsService.GetConnectedAccountingProvider().then((result: Integration | null) => {
            if (result !== null) {
                this.accountingProvider = result.provider.toString();
                // @ts-ignore TS2345
                this.accountingSync.GetContacts(null, true).then((contacts) => {
                    this.accountingContacts = contacts;
                });
            }
        });
    }

    getContactLink() {
        if (this.accountingContacts && this.customer.AccountingId) {
            return this.accountingContacts.find((c: AccountingContact) => {
                return c.id === this.customer.AccountingId;
            });
        }
    }

    removeContactLink() {
        this.customer.AccountingId = null;
    }

    // @ts-ignore TS7006
    handleAddressChange(event: google.maps.places.Place) {
        const addressComponents = getAddressComponents(event);
        const state = this.sharedData.Common.StatesInfo.find(
            // @ts-ignore TS7006
            (s) => s.code === addressComponents.stateName || s.name === addressComponents.stateName
        );

        this.customer.Address = addressComponents.addressLine1;
        this.customer.CityTown = addressComponents.city;
        this.customer.State = state ? state.value : state;
        this.customer.PostCode = addressComponents.postcode;
    }

    private setPermissions() {
        this.permissionService
            .getStates([
                'customers.maintain-customers.edit-customer-details',
                'customers.maintain-customer-contacts.view-contacts',
                'customers.maintain-customer-contacts.add-contacts',
                'customers.maintain-customer-documents.view-documents',
                'customers.maintain-customer-documents.add-documents',
                'customers.maintain-customer-documents.delete-documents',
                'customers.maintain-customer-notes.view-notes',
                'customers.maintain-customer-notes.add-notes',
                'customers.maintain-customer-notes.delete-notes',
            ])
            .subscribe((states) => {
                this.permissions = {
                    // @ts-ignore TS2322
                    editCustomerDetails: states['customers.maintain-customers.edit-customer-details'],
                    // @ts-ignore TS2322
                    viewContacts: states['customers.maintain-customer-contacts.view-contacts'],
                    // @ts-ignore TS2322
                    addContacts: states['customers.maintain-customer-contacts.add-contacts'],
                    // @ts-ignore TS2322
                    viewDocuments: states['customers.maintain-customer-documents.view-documents'],
                    // @ts-ignore TS2322
                    addDocuments: states['customers.maintain-customer-documents.add-documents'],
                    // @ts-ignore TS2322
                    deleteDocuments: states['customers.maintain-customer-documents.delete-documents'],
                    // @ts-ignore TS2322
                    viewNotes: states['customers.maintain-customer-notes.view-notes'],
                    // @ts-ignore TS2322
                    addNotes: states['customers.maintain-customer-notes.add-notes'],
                    // @ts-ignore TS2322
                    deleteNotes: states['customers.maintain-customer-notes.delete-notes'],
                };
            });
    }
}
